import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserPlus, ArrowLeft, ArrowRight } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";

export default function Register() {
  const { t, lang, toggleLang } = useApp();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const Arrow = lang === "ar" ? ArrowRight : ArrowLeft;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error(lang === "ar" ? "كلمة المرور قصيرة (٦ أحرف على الأقل)" : "Password too short (min 6 chars)");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/register", form);
      localStorage.setItem("zayd_token", data.token);
      window.location.href = "/";  // hard reload so AppContext picks up the user
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 relative overflow-hidden">
      <div className="absolute inset-y-0 end-0 hidden lg:block w-1/2 bg-primary">
        <div className="hero-noise absolute inset-0 opacity-80" />
        <div className="relative h-full flex flex-col justify-end p-12 text-primary-foreground">
          <h2 className="text-4xl font-bold leading-tight">{t.brand}</h2>
          <p className="mt-3 text-white/80 max-w-md">{t.heroSub}</p>
        </div>
      </div>

      <div className="relative w-full max-w-md bg-surface border border-border rounded-2xl p-8 shadow-floating z-10 lg:me-auto lg:ms-12">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6">
          <Arrow className="h-4 w-4" />
          {t.backToStorefront}
        </Link>
        <div className="flex items-center gap-2 mb-2">
          <BrandLogo size={36} />
          <span className="text-lg font-bold">{t.brand}</span>
          <button onClick={toggleLang} className="ms-auto text-xs text-muted-foreground hover:text-foreground" data-testid="register-language-switcher">
            {t.language}
          </button>
        </div>
        <h1 className="text-2xl font-bold mb-1">{lang === "ar" ? "إنشاء حساب" : "Create an account"}</h1>
        <p className="text-sm text-muted-foreground mb-6">
          {lang === "ar" ? "سجّل لتتبع طلباتك وتحميل ملفاتك بسهولة." : "Register to track your orders and download your files."}
        </p>

        <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
          <div>
            <Label htmlFor="rname">{t.customerName}</Label>
            <Input id="rname" data-testid="register-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required />
          </div>
          <div>
            <Label htmlFor="remail">{t.email}</Label>
            <Input id="remail" type="email" data-testid="register-email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required />
          </div>
          <div>
            <Label htmlFor="rpwd">{t.password}</Label>
            <Input id="rpwd" type="password" minLength={6} data-testid="register-password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} required />
            <p className="text-[11px] text-muted-foreground mt-1">{lang === "ar" ? "٦ أحرف على الأقل" : "Minimum 6 characters"}</p>
          </div>
          <Button type="submit" className="w-full gap-2" size="lg" disabled={submitting} data-testid="register-submit">
            <UserPlus className="h-4 w-4" />
            {submitting ? "..." : (lang === "ar" ? "إنشاء حساب" : "Create account")}
          </Button>
        </form>

        <p className="text-sm text-muted-foreground mt-6 text-center">
          {lang === "ar" ? "لديك حساب؟" : "Already have an account?"}{" "}
          <Link to="/login" className="text-primary font-medium hover:underline" data-testid="register-login-link">
            {t.signIn}
          </Link>
        </p>
      </div>
    </div>
  );
}
